import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { BrandKitState } from '../BrandKitCreator';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

interface Props {
  brandKit: BrandKitState;
  onBack: () => void;
}

const DownloadKitTab: React.FC<Props> = ({ brandKit, onBack }) => {
  const [downloading, setDownloading] = useState(false);

  const downloadLogo = async () => {
    try {
      const link = document.createElement('a');
      link.href = brandKit.logoUrl;
      link.download = `${brandKit.businessName.replace(/\s+/g, '-')}-logo.png`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Logo downloaded!');
    } catch (error) {
      toast.error('Failed to download logo');
    }
  };

  const copySignature = async () => {
    try {
      const blob = new Blob([brandKit.signatureHtml], { type: 'text/html' });
      const clipboardItem = new ClipboardItem({ 'text/html': blob });
      await navigator.clipboard.write([clipboardItem]);
      toast.success('Signature copied to clipboard!');
    } catch (error) {
      const textarea = document.createElement('textarea');
      textarea.value = brandKit.signatureHtml;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success('Signature HTML copied!');
    }
  };

  const downloadCompleteKit = async () => {
    try {
      setDownloading(true);
      const zip = new JSZip();

      // 1. Business Info
      const businessInfo = `
BUSINESS NAME: ${brandKit.businessName}
TAGLINE: ${brandKit.tagline}
INDUSTRY: ${brandKit.industry}
DESCRIPTION: ${brandKit.description}

PRIMARY COLOR: ${brandKit.primaryColor}
ACCENT COLOR: ${brandKit.accentColor}

GENERATED: ${new Date().toLocaleDateString()}
      `.trim();
      zip.file('business-info.txt', businessInfo);

      // 2. Logo (try to fetch and include)
      try {
        const logoResponse = await fetch(brandKit.logoUrl);
        const logoBlob = await logoResponse.blob();
        zip.file(`${brandKit.businessName.replace(/\s+/g, '-')}-logo.png`, logoBlob);
      } catch (error) {
        console.error('Could not include logo in ZIP:', error);
        zip.file('logo-url.txt', brandKit.logoUrl);
      }

      // 3. Email Signature
      zip.file('email-signature.html', brandKit.signatureHtml);

      // 4. Brand Colors
      const colorsHtml = `
<!DOCTYPE html>
<html>
<head>
  <title>${brandKit.businessName} - Brand Colors</title>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; }
    .color-box { 
      width: 200px; 
      height: 200px; 
      border-radius: 8px; 
      margin: 20px 0;
      box-shadow: 0 4px 6px rgba(0,0,0,0.1);
    }
    .color-info { 
      margin-top: 10px;
      font-size: 18px;
      font-weight: bold;
    }
  </style>
</head>
<body>
  <h1>${brandKit.businessName} Brand Colors</h1>
  
  <h2>Primary Color</h2>
  <div class="color-box" style="background-color: ${brandKit.primaryColor};"></div>
  <div class="color-info">HEX: ${brandKit.primaryColor}</div>
  
  <h2>Accent Color</h2>
  <div class="color-box" style="background-color: ${brandKit.accentColor};"></div>
  <div class="color-info">HEX: ${brandKit.accentColor}</div>
</body>
</html>
      `.trim();
      zip.file('brand-colors.html', colorsHtml);

      // 5. README
      const readme = `
${brandKit.businessName} - Brand Kit
=======================================

This brand kit was generated by Explanatum's Brand Kit Creator.

Contents:
- business-info.txt: Your business name, tagline, and description
- ${brandKit.businessName.replace(/\s+/g, '-')}-logo.png: Your AI-generated logo
- email-signature.html: Your professional email signature (ready to paste)
- brand-colors.html: Visual reference for your brand colors

How to use:
1. Logo: Use in your website, social media, business cards, etc.
2. Email Signature: Copy the HTML from email-signature.html and paste into your email client's signature settings
3. Brand Colors: Reference these colors in all your marketing materials for consistency

Need help? Visit https://explanatum.com

Generated: ${new Date().toLocaleDateString()}
      `.trim();
      zip.file('README.txt', readme);

      // Generate ZIP
      const zipBlob = await zip.generateAsync({ type: 'blob' });
      saveAs(zipBlob, `${brandKit.businessName.replace(/\s+/g, '-')}-brand-kit.zip`);
      
      toast.success('Complete brand kit downloaded!');
    } catch (error) {
      console.error('Error creating ZIP:', error);
      toast.error('Failed to create brand kit. Please try downloading items individually.');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Step 4: Download Your Brand Kit</h2>
          <p className="text-gray-600">
            Your complete brand identity is ready!
          </p>
        </div>
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-900 transition"
        >
          ← Back
        </button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Business Name Card */}
        <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 border border-orange-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Business Name</h3>
          </div>
          <p className="text-2xl font-bold text-gray-900 mb-2">{brandKit.businessName}</p>
          <p className="text-sm text-gray-600 italic">"{brandKit.tagline}"</p>
        </div>

        {/* Logo Card */}
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 border border-blue-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Logo</h3>
          </div>
          <div className="aspect-square bg-white rounded-lg p-4 flex items-center justify-center">
            <img src={brandKit.logoUrl} alt="Logo" className="max-w-full max-h-full object-contain" />
          </div>
          <button
            onClick={downloadLogo}
            className="mt-4 w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition text-sm font-medium"
          >
            Download Logo
          </button>
        </div>

        {/* Email Signature Card */}
        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 border border-green-200">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-bold text-gray-900">Email Signature</h3>
          </div>
          <div className="bg-white rounded-lg p-4 border border-gray-200 max-h-48 overflow-auto">
            <div dangerouslySetInnerHTML={{ __html: brandKit.signatureHtml }} />
          </div>
          <button
            onClick={copySignature}
            className="mt-4 w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition text-sm font-medium"
          >
            Copy Signature
          </button>
        </div>
      </div>

      {/* Brand Colors */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">Brand Colors</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-gray-600 mb-2">Primary Color</p>
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-16 rounded-lg border-2 border-gray-200"
                style={{ backgroundColor: brandKit.primaryColor }}
              />
              <span className="font-mono font-bold text-gray-900">{brandKit.primaryColor}</span>
            </div>
          </div>
          <div>
            <p className="text-sm text-gray-600 mb-2">Accent Color</p>
            <div className="flex items-center gap-3">
              <div
                className="w-16 h-16 rounded-lg border-2 border-gray-200"
                style={{ backgroundColor: brandKit.accentColor }}
              />
              <span className="font-mono font-bold text-gray-900">{brandKit.accentColor}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Download Complete Kit Button */}
      <div className="bg-gradient-to-r from-orange-500 to-pink-600 rounded-xl p-8 text-center text-white">
        <h3 className="text-2xl font-bold mb-2">Download Complete Brand Kit</h3>
        <p className="mb-6 opacity-90">
          Get everything in one convenient ZIP file: business info, logo, email signature, and brand colors
        </p>
        <button
          onClick={downloadCompleteKit}
          disabled={downloading}
          className="px-8 py-4 bg-white text-orange-600 font-bold rounded-lg hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-3"
        >
          {downloading ? (
            <>
              <svg className="animate-spin h-6 w-6" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Creating ZIP...
            </>
          ) : (
            <>
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
              </svg>
              Download Complete Kit (.ZIP)
            </>
          )}
        </button>
      </div>

      {/* Next Steps */}
      <div className="bg-blue-50 rounded-xl p-6 border border-blue-200">
        <h3 className="text-lg font-bold text-gray-900 mb-3">🎉 Next Steps</h3>
        <ul className="space-y-2 text-gray-700">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">1.</span>
            <span>Use your logo on your website, social media, and business cards</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">2.</span>
            <span>Set up your email signature in Gmail, Outlook, or your email client</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">3.</span>
            <span>Apply your brand colors consistently across all marketing materials</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">4.</span>
            <span>Consider creating business cards, letterhead, and other branded materials</span>
          </li>
        </ul>
      </div>
    </div>
  );
};

export default DownloadKitTab;
